import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { userService } from "../../_services";
import logo from "../../images/logo.png";

export default function AppHeader({ isAdmin, isConsumer }) {
  const navigate = useNavigate();
  const [isLogout, setIsLogout] = useState(false);
  const connectionRef = useRef(null);
  
  // Get user data from localStorage with error handling
  let userRole = '';
  let shortName = '';
  let fullName = '';
  let firstName = '';
  
  try {
    const roleData = localStorage.getItem('UserRole');
    if (roleData) {
      userRole = window.atob(roleData);
    }
    
    const userDetailData = localStorage.getItem('hacker_user_detail');
    if (userDetailData) {
      const userDetail = window.atob(userDetailData);
      [shortName, fullName] = userDetail.split(':');
      firstName = fullName?.split(' ')[0] || shortName;
    }
  } catch (error) {
    console.error('Error reading user data:', error);
  }

  // Setup SignalR for real-time deactivation notifications
  useEffect(() => {
    const setupSignalR = async () => {
      if (connectionRef.current) return; // Already connected

      const connection = new signalR.HubConnectionBuilder()
        .withUrl("/deactivatedUserHub", {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets
        })
        .build();

      try {
        await connection.start();
        
        connection.on("ReceiveMessage", (Email1, status, UserId, minset) => {
          let Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id') || '');
          if (UserId === Usermeta_Data.split(':')[0] && 
              status === "Deactivate" && 
              (minset[0]?.userType === "" || minset[0]?.userType === "consumer")) {
            handleLogout();
          }
        });

        connectionRef.current = connection;
      } catch (err) {
        console.error('SignalR connection error:', err);
      }
    };

    setupSignalR();

    // Cleanup on unmount
    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
      }
    };
  }, []);

  const handleLogout = () => {
    userService.logout();
    setIsLogout(true);
    navigate('/signup');
  };

  const handleAccountClick = () => {
    const accountPath = isConsumer ? '/consumer/account' : '/account';
    navigate(accountPath);
  };


  if (isLogout) {
    return null;
  }

  return (
    <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center justify-between gap-2 border-b transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-16 bg-card px-4 w-full">
      <div className="flex items-center gap-2">
        <SidebarTrigger className="-ml-1" />
        <Link to={isAdmin ? "/Admin/groups" : isConsumer ? "/consumer/summary" : "/summary"} className="flex items-center">
          <img src={logo} alt="RxHacker Pro" className="w-56" />
        </Link>
      </div>

      {/* Spacer */}
      <div className="ml-auto" />

      {/* User Profile — dropdown only for consumers; admin/distributor use the sidebar */}
      {isConsumer ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="relative flex items-center gap-2 hover:bg-transparent text-primary hover:text-accent transition-colors duration-200"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                  {shortName}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:flex flex-col items-start text-left">
                <span className="text-sm font-medium">{`Hey, ${firstName}`}</span>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="flex flex-col space-y-1 p-2">
              <p className="text-sm font-medium leading-none">{fullName}</p>
              <p className="text-xs leading-none text-muted-foreground">{userRole}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleAccountClick}
              className="text-primary focus:bg-transparent focus:text-accent hover:text-accent cursor-pointer transition-colors duration-200"
            >
              Account Info
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleLogout}
              className="text-primary focus:bg-transparent focus:text-accent hover:text-accent cursor-pointer transition-colors duration-200"
            >
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        /* Admin / Distributor — account info & logout live in the sidebar */
        <div className="flex items-center gap-2 text-primary">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
              {shortName}
            </AvatarFallback>
          </Avatar>
          <span className="hidden sm:block text-sm font-medium">{fullName}</span>
        </div>
      )}
    </header>
  );
}

